import FeatureGates from '@atlaskit/feature-gate-js-client';
import { addFeatureFlagAccessed } from '@atlaskit/react-ufo/feature-flags-accessed';
import { editorExperimentsConfig } from './experiments-config';
import { _overrides, _paramOverrides, _product } from './setup';
function expValInternal(_ref) {
  var _paramOverrides2, _experimentConfig$pro;
  var experimentName = _ref.experimentName,
    experimentParam = _ref.experimentParam,
    defaultValue = _ref.defaultValue,
    fireExperimentExposure = _ref.fireExperimentExposure;
  var experimentConfig = editorExperimentsConfig[experimentName];
  if (experimentConfig === undefined) {
    // Warning! If a product is improperly configured (ie. their editor packages are misaligned)
    // it can cause the editor to crash here or if the experiment does not exist.
    // We will definitely crash via any code path later in this function - this just makes the error explicit and clear.
    throw new Error("Editor experiment configuration is not defined ".concat(experimentName, ". Likely the experiment does not exist or editor package versions are misaligned"));
  }

  // @ts-ignore need to loosen the type here to allow for any experiment name
  if (_overrides[experimentName] !== undefined) {
    // This will be hit in the case of a test setting an override
    // @ts-ignore need to loosen the type here to allow for any experiment name
    return _overrides[experimentName];
  }

  // Check for parameter overrides
  var paramOverride = (_paramOverrides2 = _paramOverrides[experimentName]) === null || _paramOverrides2 === void 0 ? void 0 : _paramOverrides2[experimentParam];
  if (paramOverride !== undefined) {
    return paramOverride;
  }

  // If client is not initialized, we return the default value
  if (!FeatureGates.initializeCompleted()) {
    return defaultValue;
  }
  if (!_product) {
    // This will be hit in the case of a product not having setup the editor experiment tooling
    return defaultValue;
  }

  // Typescript is complaining here about accessing the productKeys property
  // Ignored via go/ees005
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  var experimentKey = experimentConfig === null || experimentConfig === void 0 || (_experimentConfig$pro = experimentConfig.productKeys) === null || _experimentConfig$pro === void 0 ? void 0 : _experimentConfig$pro[_product];
  if (!experimentKey) {
    // This will be hit in the case of an experiment not being set up for the product
    return defaultValue;
  }

  // eslint-disable-next-line @atlaskit/platform/use-recommended-utils
  var experimentValue = FeatureGates.getExperimentValue(experimentName, experimentParam, defaultValue, {
    fireExperimentExposure: fireExperimentExposure
  });
  if (
  // eslint-disable-next-line @atlaskit/platform/use-recommended-utils
  FeatureGates.getExperimentValue('cc_editor_experiments_ufo_gate_reporting_expval', 'isEnabled', false)) {
    // Duplicated from /confluence/next/packages/feature-experiments/src/index.ts
    addFeatureFlagAccessed("".concat(experimentName, ":").concat(experimentParam), experimentValue);
  }
  return experimentValue;
}

/**
 * Use to check a any param value for an experiment
 *
 * **Note**: this will return the default value when the experiment;
 * - is not being served to the client (ie. pre start)
 * - or is not configured in experiments-config
 *
 * If you need to check a param value without an exposure check see {@link expParamEqualsNoExposure}
 *
 * @example
 * ```ts
 * const delay = expParamEquals('experiment-name', 'param-name', defaultValue)
 * await new Promise(res => setTimeout(res, delay)
 * ```
 */
export function expVal(experimentName, experimentParam, defaultValue) {
  return expValInternal({
    experimentName: experimentName,
    experimentParam: experimentParam,
    defaultValue: defaultValue,
    fireExperimentExposure: true
  });
}

/**
 * Use to check a any param value for an experiment without firing an exposure event
 *
 * **Note**: this will return the default value when the experiment;
 * - is not being served to the client (ie. pre start)
 * - or is not configured in experiments-config
 *
 * @example
 * ```ts
 * const delay = expParamEqualsNoExposure('experiment-name', 'param-name', defaultValue)
 * await new Promise(res => setTimeout(res, delay)
 * ```
 */
export function expValNoExposure(experimentName, experimentParam, defaultValue) {
  return expValInternal({
    experimentName: experimentName,
    experimentParam: experimentParam,
    defaultValue: defaultValue,
    fireExperimentExposure: false
  });
}